/**
 * RunDetailHeader — Lauf-Kopf der Run-Detail-Seite (T7, aus `TextRunDetailPage`
 * extrahiert wegen des 100-Zeilen-Limits). Badge, Meta-Zeile (Paare/fehlgeschlagen/
 * Credits E3 „~"), Progressbar, Retry-Button bei failed/completed_with_errors.
 */
import { useTranslation } from 'react-i18next'

import { Badge, Progress } from '@/components/v2/ui'
import { EID } from '@/lib/element-ids.registry'
import { formatNumber } from '@/lib/utils'
import type { ChannelContentRun } from '@/lib/api/channel-content-bulk'

import { useTextRunMutations } from './hooks/useSuggestionMutations'
import { isRunActive } from './hooks/runStatus'

const RETRYABLE = new Set(['failed', 'completed_with_errors'])

export function RunDetailHeader({ run }: { run: ChannelContentRun }) {
  const { t } = useTranslation()
  const { retry } = useTextRunMutations()
  const percentage = run.total_pairs > 0 ? (run.done_pairs / run.total_pairs) * 100 : 0

  return (
    <div className="text-bulk-review-header space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-bold text-neutral-900 dark:text-neutral-100">{t('channelContent.title')}</h1>
        <Badge variant={run.status === 'completed_with_errors' ? 'warning' : 'default'} size="sm">
          {t(`channelContent.runs.status.${run.status}`)}
        </Badge>
      </div>

      <div className="flex flex-wrap items-center gap-4 text-sm text-neutral-500 dark:text-neutral-400">
        <span>{t('channelContent.runs.progress', { done: run.done_pairs, total: run.total_pairs })}</span>
        {run.failed_pairs > 0 && <span>{t('channelContent.runs.failedPairs', { count: run.failed_pairs })}</span>}
        <span>{t('channelContent.runs.creditsEstimated', { count: formatNumber(run.credits_estimated) })}</span>
        {run.status !== 'pending' && (
          <span>{t('channelContent.runs.creditsSpent', { count: formatNumber(run.credits_spent) })}</span>
        )}
        {RETRYABLE.has(run.status) && (
          <button
            type="button"
            id={EID['text-bulk-review-retry-btn']}
            disabled={retry.isPending}
            onClick={() => retry.mutate(run.id)}
            className="rounded-md border border-orange-300 px-2.5 py-1 text-xs font-medium text-orange-600 hover:bg-orange-50 disabled:opacity-50 dark:border-orange-700 dark:text-orange-400 dark:hover:bg-orange-950/20"
          >
            {t('channelContent.runs.retry')}
          </button>
        )}
      </div>

      <Progress value={percentage} size="sm" variant={isRunActive(run.status) ? 'default' : 'success'} />
    </div>
  )
}
